/**
 * LLM Parameter Helpers
 * Utilities for managing, validating, and optimizing LLM parameters
 */

export interface LLMParameters {
  temperature: number;
  max_tokens: number;
  top_p: number;
  presence_penalty: number;
  frequency_penalty: number;
}

export interface ParameterConfig {
  name: keyof LLMParameters;
  label: string;
  description: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  recommendedMin: number;
  recommendedMax: number;
  unit?: string;
}

export const PARAMETER_CONFIGS: ParameterConfig[] = [
  {
    name: 'temperature',
    label: 'Temperature',
    description: 'Controls randomness. Lower values make output more focused and deterministic, higher values make it more creative and diverse.',
    min: 0,
    max: 2,
    step: 0.1,
    defaultValue: 0.7,
    recommendedMin: 0.3,
    recommendedMax: 1.0,
    unit: ''
  },
  {
    name: 'max_tokens',
    label: 'Max Tokens',
    description: 'Maximum number of tokens to generate. Directly affects cost. Lower values reduce cost but may truncate output.',
    min: 1000,
    max: 16000,
    step: 500,
    defaultValue: 6000,
    recommendedMin: 4000,
    recommendedMax: 8000,
    unit: ''
  },
  {
    name: 'top_p',
    label: 'Top P (Nucleus Sampling)',
    description: 'Controls diversity via nucleus sampling. 1.0 considers all tokens, lower values make output more focused.',
    min: 0,
    max: 1,
    step: 0.05,
    defaultValue: 1,
    recommendedMin: 0.85,
    recommendedMax: 1.0,
    unit: ''
  },
  {
    name: 'presence_penalty',
    label: 'Presence Penalty',
    description: 'Penalizes tokens that have appeared in the text so far. Positive values encourage new topics.',
    min: -2,
    max: 2,
    step: 0.1,
    defaultValue: 0,
    recommendedMin: 0,
    recommendedMax: 0.5,
    unit: ''
  },
  {
    name: 'frequency_penalty',
    label: 'Frequency Penalty',
    description: 'Penalizes tokens based on their frequency. Positive values reduce repetition.',
    min: -2,
    max: 2,
    step: 0.1,
    defaultValue: 0,
    recommendedMin: 0,
    recommendedMax: 0.5,
    unit: ''
  }
];

export const DEFAULT_PARAMETERS: LLMParameters = {
  temperature: 0.7,
  max_tokens: 6000,
  top_p: 1,
  presence_penalty: 0,
  frequency_penalty: 0
};

export interface ParameterPreset {
  name: string;
  description: string;
  parameters: LLMParameters;
  useCase: string;
}

export const SYSTEM_PRESETS: ParameterPreset[] = [
  {
    name: 'Cost Optimized',
    description: 'Minimizes token usage and cost while maintaining acceptable quality',
    parameters: {
      temperature: 0.3,
      max_tokens: 4000,
      top_p: 0.9,
      presence_penalty: 0,
      frequency_penalty: 0
    },
    useCase: 'general'
  },
  {
    name: 'Balanced',
    description: 'Optimal balance between quality, speed, and cost',
    parameters: {
      temperature: 0.7,
      max_tokens: 6000,
      top_p: 1,
      presence_penalty: 0,
      frequency_penalty: 0
    },
    useCase: 'general'
  },
  {
    name: 'Quality Optimized',
    description: 'Maximizes output quality and creativity, higher token usage',
    parameters: {
      temperature: 0.8,
      max_tokens: 8000,
      top_p: 1,
      presence_penalty: 0.1,
      frequency_penalty: 0.1
    },
    useCase: 'general'
  },
  {
    name: 'Conservative',
    description: 'Predictable, focused outputs with minimal creativity',
    parameters: {
      temperature: 0.1,
      max_tokens: 4000,
      top_p: 0.8,
      presence_penalty: 0,
      frequency_penalty: 0
    },
    useCase: 'analysis'
  },
  {
    name: 'Creative',
    description: 'Encourages diverse and creative outputs',
    parameters: {
      temperature: 1.0,
      max_tokens: 8000,
      top_p: 1,
      presence_penalty: 0.3,
      frequency_penalty: 0.3
    },
    useCase: 'coaching'
  }
];

/**
 * Validates if parameters are within acceptable ranges
 */
export function validateParameters(parameters: Partial<LLMParameters>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  PARAMETER_CONFIGS.forEach(config => {
    const value = parameters[config.name];
    if (value !== undefined) {
      if (value < config.min || value > config.max) {
        errors.push(`${config.label} must be between ${config.min} and ${config.max}`);
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Estimates token cost based on parameters and model
 */
export function estimateTokenCost(
  maxTokens: number,
  model: string
): {
  estimatedInputCost: number;
  estimatedOutputCost: number;
  estimatedTotalCost: number;
} {
  // Approximate costs per 1M tokens for common models (as of 2025)
  const modelCosts: Record<string, { input: number; output: number }> = {
    'openai/gpt-4o': { input: 2.50, output: 10.00 },
    'openai/gpt-4o-mini': { input: 0.15, output: 0.60 },
    'anthropic/claude-3-5-sonnet': { input: 3.00, output: 15.00 },
    'anthropic/claude-3-5-haiku': { input: 0.80, output: 4.00 },
    'anthropic/claude-3-opus': { input: 15.00, output: 75.00 }
  };

  // Default to mid-range costs if model not found
  const costs = modelCosts[model] || { input: 2.00, output: 8.00 };

  // Assume average prompt is 2000 tokens for estimation
  const avgPromptTokens = 2000;

  const estimatedInputCost = (avgPromptTokens / 1000000) * costs.input;
  const estimatedOutputCost = (maxTokens / 1000000) * costs.output;
  const estimatedTotalCost = estimatedInputCost + estimatedOutputCost;

  return {
    estimatedInputCost,
    estimatedOutputCost,
    estimatedTotalCost
  };
}

/**
 * Calculates optimization score based on parameters
 */
export function calculateParameterOptimizationScore(
  parameters: LLMParameters,
  metrics?: {
    successRate?: number;
    avgResponseTime?: number;
    avgCost?: number;
  }
): number {
  let score = 50;

  // Token efficiency (30 points)
  const tokenEfficiency = 1 - (parameters.max_tokens / 16000);
  score += tokenEfficiency * 30;

  // Temperature optimization (20 points)
  // Sweet spot is 0.5-0.8 for most use cases
  const tempOptimal = parameters.temperature >= 0.5 && parameters.temperature <= 0.8;
  score += tempOptimal ? 20 : (20 - Math.abs(parameters.temperature - 0.65) * 20);

  // If we have metrics, use them (50 points)
  if (metrics) {
    if (metrics.successRate !== undefined) {
      score += (metrics.successRate / 100) * 25;
    }
    if (metrics.avgResponseTime !== undefined) {
      const responseScore = Math.max(0, 1 - (metrics.avgResponseTime / 10000));
      score += responseScore * 15;
    }
    if (metrics.avgCost !== undefined) {
      const costScore = Math.max(0, 1 - (metrics.avgCost / 0.10));
      score += costScore * 10;
    }
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Generates optimization recommendations based on usage patterns
 */
export function generateParameterRecommendations(
  currentParameters: LLMParameters,
  metrics: {
    avgTokensUsed: number;
    maxTokensNeeded: number;
    successRate: number;
    avgCost: number;
  }
): {
  recommendedParameters: Partial<LLMParameters>;
  reasoning: string[];
  potentialSavings: number;
} {
  const recommendations: Partial<LLMParameters> = {};
  const reasoning: string[] = [];
  let potentialSavings = 0;

  // Optimize max_tokens based on actual usage
  if (metrics.maxTokensNeeded < currentParameters.max_tokens * 0.7) {
    const recommendedMaxTokens = Math.ceil(metrics.maxTokensNeeded * 1.2 / 500) * 500;
    recommendations.max_tokens = Math.max(2000, recommendedMaxTokens);
    const tokenReduction = currentParameters.max_tokens - recommendations.max_tokens;
    potentialSavings += (tokenReduction / 1000000) * 10; // Rough estimate
    reasoning.push(
      `Reduce max_tokens from ${currentParameters.max_tokens} to ${recommendations.max_tokens}. ` +
      `Your outputs typically use ${metrics.avgTokensUsed} tokens, so this provides adequate headroom.`
    );
  }

  // Optimize temperature based on success rate
  if (metrics.successRate < 90 && currentParameters.temperature > 0.8) {
    recommendations.temperature = 0.7;
    reasoning.push(
      `Lower temperature from ${currentParameters.temperature} to 0.7 to improve consistency. ` +
      `Current success rate (${metrics.successRate}%) suggests outputs are too variable.`
    );
  }

  // Suggest reducing temperature if it's too high for no reason
  if (currentParameters.temperature > 1.0 && metrics.successRate >= 95) {
    recommendations.temperature = 0.8;
    reasoning.push(
      `Temperature of ${currentParameters.temperature} is higher than necessary. ` +
      `Reducing to 0.8 will maintain quality while improving cost efficiency.`
    );
  }

  // If no recommendations, maintain current settings
  if (Object.keys(recommendations).length === 0) {
    reasoning.push('Current parameters are well-optimized based on usage patterns.');
  }

  return {
    recommendedParameters: recommendations,
    reasoning,
    potentialSavings: Math.round(potentialSavings * 100) / 100
  };
}

/**
 * Formats parameters for display
 */
export function formatParameters(parameters: LLMParameters): string {
  return Object.entries(parameters)
    .map(([key, value]) => {
      const config = PARAMETER_CONFIGS.find(c => c.name === key);
      return `${config?.label || key}: ${value}${config?.unit || ''}`;
    })
    .join(', ');
}

/**
 * Merges parameters with defaults
 */
export function mergeWithDefaults(
  parameters: Partial<LLMParameters>
): LLMParameters {
  return {
    ...DEFAULT_PARAMETERS,
    ...parameters
  };
}

/**
 * Checks if parameters are in recommended range
 */
export function isInRecommendedRange(
  parameter: keyof LLMParameters,
  value: number
): boolean {
  const config = PARAMETER_CONFIGS.find(c => c.name === parameter);
  if (!config) return true;

  return value >= config.recommendedMin && value <= config.recommendedMax;
}

/**
 * Gets all parameters that are outside recommended ranges
 */
export function getOutOfRangeParameters(
  parameters: LLMParameters
): Array<{ parameter: string; value: number; recommended: string }> {
  const outOfRange: Array<{ parameter: string; value: number; recommended: string }> = [];

  PARAMETER_CONFIGS.forEach(config => {
    const value = parameters[config.name];
    if (!isInRecommendedRange(config.name, value)) {
      outOfRange.push({
        parameter: config.label,
        value,
        recommended: `${config.recommendedMin} - ${config.recommendedMax}`
      });
    }
  });

  return outOfRange;
}
